import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { DataService } from '../../services/data.service';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { TipsideModalComponent } from '../modals/tipside-modal/tipside-modal.component';
import { MeasurementDTO, MedicalDataDTO } from '../../classes/api-dto';
import { ModalWithTwoButtonsComponent } from '../modals/modal-with-two-buttons/modal-with-two-buttons.component';
import { CommonModule, DatePipe } from '@angular/common';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzCardComponent, NzCardModule } from 'ng-zorro-antd/card';
import { NzBadgeComponent, NzRibbonComponent } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ConsultTypeService } from '../../services/consult-type.service';
import { AppointmentStatusService } from '../../services/appointment-status.service';

@Component({
  selector: 'app-card-next-appointment',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzModalModule, NzGridModule, NzCardComponent, NzIconModule, NzRibbonComponent, NzBadgeComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [DatePipe],
  templateUrl: './card-next-appointment.component.html',
  styleUrl: './card-next-appointment.component.scss',
})
export class CardNextAppointmentComponent implements OnInit {
  @Input() appointments: any;
  isModalVisible: boolean = false;
  badgeStatus: 'success' | 'error' = 'success';
  radioElement: string[] = ['success', 'error'];
  medicalData!: MedicalDataDTO;
  @Output() getAppointments: EventEmitter<boolean> = new EventEmitter(false);
  isPreviewMode: boolean = false;
  calendarUrl: string = '';

  constructor(
    public http: HttpService,
    public dataService: DataService,
    public router: Router,
    public translateService: TranslateService,
    public modal: NzModalService,
    public datePipe: DatePipe,
    public cdr: ChangeDetectorRef,
    private consultTypeService: ConsultTypeService,
    private statusService: AppointmentStatusService
  ) {}

  ngOnInit() {
    this.dataService.getCalendarUrl().subscribe((res) => {
      this.calendarUrl = res;
    });
    this.isPreviewMode = this.router.url.startsWith('/preview-page');
    if (!this.isPreviewMode) {
      this.setMedicalProfileData();
    }
  }

  getTipologia(consultType: string): string {
    return this.consultTypeService.getTipologia(consultType);
  }

  getPrestazione(consultType: string): string {
    return this.consultTypeService.getPrestazione(consultType);
  }

  getTranslationKey(status: string): string {
    return this.statusService.getTranslationKey(status);
  }

  isStatusVisible(status: string): boolean {
    return this.statusService.isVisibleToClient(status);
  }

  getBadgeStatus(status: string): string {
    switch (status) {
      case 'Confirmed':
      case 'CompletedWritten':
      case 'CompletedVideo':
      case 'CompletedPhone':
      case 'Televisit':
        return 'success';
      case 'Canceled':
      case 'CustomerNotAvailable':
      case 'Rejected':
      case 'SupplementaryDocumentationNeeded':
      case 'VideoCallProblemConnection':
      case 'CallDandledByJunoOperator':
        return 'error';
      case 'ReadyToAssist':
      case 'Awaiting':
      case 'DocumentsProvided':
      case 'NewProposals':
      case 'SaveNpsData':
      default:
        return 'warning';
    }
  }

  getBadgeBackground(status: string): any {
    let string = 'background-color: ';
    switch (status) {
      case 'Confirmed':
      case 'CompletedWritten':
      case 'CompletedVideo':
      case 'CompletedPhone':
      case 'Televisit':
        return string + '#E5F2EB;' + 'border: 1px solid #219653';
      case 'Canceled':
      case 'CustomerNotAvailable':
      case 'Rejected':
      case 'SupplementaryDocumentationNeeded':
      case 'VideoCallProblemConnection':
      case 'CallDandledByJunoOperator':
        return string + '#F8E0E5;' + 'border: 1px solid #C80226';
      case 'ReadyToAssist':
      case 'Awaiting':
      case 'DocumentsProvided':
      case 'NewProposals':
      case 'SaveNpsData':
      default:
        return string + '#FDF8E9;' + 'border: 1px solid #F2C94C';
    }
  }

  openModal(link: string): void {
    if (link) {
      window.open(link, '_blank', 'noopener');
    } else {
      this.isModalVisible = true;
    }
  }

  closeModal(): void {
    this.isModalVisible = false;
  }

  toggleEditMode(appointment: any): void {
    const token = localStorage.getItem('authToken')?.replaceAll('"', '');
    const today = new Date();
    const body = {
      dossierCode: appointment.dossierCode,
      language: 'it',
      startingMonth: today.getUTCMonth() + 1,
      startingYear: today.getFullYear(),
      token: token,
      primaryProvision: null,
      activityCode: null,
    };

    this.http.getCalendarDatesByModify(body, this.calendarUrl).subscribe((res) => {
      if (res) {
        this.dataService.setDatesByModify(res);
        localStorage.setItem('appointment', JSON.stringify(appointment));
        const redirect = this.getRedirect(appointment.consultType);
        this.router.navigate([`/medical-consultations/${redirect}/modify`]);
      }
    });
  }
  private getRedirect(consultType: string) {
    switch (consultType) {
      case 'GenericTelevisitPhone':
      case 'GenericTelevisitVideo':
        return 'televisit-generic-booking';
      case 'SpecialistTelevisitPhone':
      case 'SpecialistTelevisitVideo':
        return 'televisit-specialist-booking';
      case 'GenericVideo':
      case 'GenericPhone':
        return 'generic-consultation';
      case 'SpecialistVideo':
      case 'SpecialistPhone':
        return 'specialist-consultation';
      case 'PsychologistVideo':
      case 'PsychologistPhone':
        return 'psyco-pathway';
      case 'TreatmentPathNutTelevisit':
        return 'nut-pathway';
      case 'CareManager':
        return 'care-manager';
      case 'VeterinarianPhone':
      case 'VeterinarianVideo':
        return 'vet-consultation';
      default:
        return;
    }
  }

  deleteAppointment(appointment: any): void {
    const textsModal = this.translateService.instant('deleteAppointmentModal');
    const activeModal = this.modal.create({
      nzContent: ModalWithTwoButtonsComponent,
      nzClosable: true,
      nzFooter: null,
      nzData: {
        dataModal: {
          colorActionButton: 'red',
          texts: {
            ...textsModal,
            first_button_pointer: 'delete',
          },
          actionButton: () => {
            this.http.deleteAppointment(appointment.individualId, appointment.dossierCode, appointment.missionId, appointment.consultType).subscribe((res) => {
              if (res) {
                activeModal.close();
                this.isModalVisible = false;
                this.getAppointments.emit(true);
              }
            });
          },
        },
      },
    });
  }

  public shareMedicalData() {
    const modalRef: NzModalRef<TipsideModalComponent> = this.modal.create({
      nzContent: TipsideModalComponent,
      nzClosable: true,
      nzFooter: null,
      nzData: {
        dataModal: { badgeStatus: this.badgeStatus, radioElement: this.radioElement },
      },
    });
    modalRef.afterClose.subscribe((result: any): void => {
      if (this.medicalData && this.medicalData.lastMeasurement && !!result) {
        let payload = this.medicalData.lastMeasurement;
        payload.disclaimer = result?.value === 'success' ? true : false;
        this.http.updateMedicalProfileData(payload).subscribe((medicalDataUpdated: MeasurementDTO) => {
          this.medicalData!.lastMeasurement = medicalDataUpdated;
          this.dataService.setMedicalData(this.medicalData!);
          this.badgeStatus = this.medicalData?.lastMeasurement?.disclaimer ? 'success' : 'error';
          this.cdr.detectChanges();
        });
      }
    });
  }

  private setMedicalProfileData(): void {
    const individualId = JSON.parse(localStorage.getItem('userData')!).individualId;
    this.http.medicalProfileData(individualId).subscribe((medicalProfileData: MedicalDataDTO) => {
      this.medicalData = medicalProfileData;
      this.dataService.setMedicalData(medicalProfileData);
      this.badgeStatus = this.medicalData.lastMeasurement?.disclaimer ? 'success' : 'error';
    });
  }

  public convertDate(date: string, isDate: boolean): string {
    if (date && isDate) {
      return this.datePipe.transform(date, 'dd/MM/yyyy')!;
    } else if (date && !isDate) {
      return this.datePipe.transform(date, 'HH:mm')!;
    }
    return '';
  }
}
